/**
 * Customer-facing copy for permission denials. See T-105.
 *
 * The API's 403 body is written for developers — "Insufficient permissions.
 * Required permission: material:update". Putting that in a toast told the user
 * nothing they could act on, and the description went on to advise contacting
 * "a super admin", which is not a role any customer can reach.
 *
 * Everything here is deliberately free of permission strings. The raw values
 * still matter for debugging, so `api.ts` logs them to the console instead.
 *
 * NOTE: `company` is surfaced as "concepts" pending T-106; if that rename lands
 * as a model rename rather than labels-only, this map moves with it.
 */

import { t } from '@lingui/core/macro';

/*
 * Both lookups are functions, not module-level maps: `t` has to run when the
 * toast is built, in the language active then, not once when this file loads.
 */

/** Resource segment of `resource:action` → the noun the UI shows users. */
function resourceNoun(resource: string): string | undefined {
  switch (resource) {
    case 'audit':
      return t`the activity log`;
    case 'company':
      return t`concepts`;
    case 'cooking-session':
      return t`cooking sessions`;
    case 'inventory':
      return t`inventory`;
    case 'location':
      return t`locations`;
    case 'material':
      return t`ingredients`;
    case 'menu':
      return t`menus`;
    case 'order':
      return t`orders`;
    case 'organization':
      return t`organizations`;
    case 'recipe':
      return t`recipes`;
    case 'region':
      return t`regions`;
    case 'role':
      return t`roles`;
    case 'scope':
      return t`user access`;
    case 'user':
      return t`users`;
    default:
      return undefined;
  }
}

/** Action segment → the verb the UI shows users. */
function actionVerb(action: string): string | undefined {
  switch (action) {
    case 'read':
      return t`view`;
    case 'create':
      return t`create`;
    case 'update':
      return t`edit`;
    case 'delete':
      return t`delete`;
    case 'write':
      return t`change`;
    case 'assign':
      return t`assign`;
    // Enforced by this app rather than the API, but it still reaches
    // `describeCapability` through the permission matrix copy.
    case 'cook':
      return t`cook`;
    default:
      return undefined;
  }
}

/**
 * Parse the permission string out of an API 403 message.
 * Exported for telemetry — never render the result to a user.
 */
export function extractMissingPermission(message?: string): string | null {
  const text = (message || '').trim();
  if (!text) return null;

  const singleMatch = text.match(/Required permission:\s*([A-Za-z0-9:*_.-]+)/i);
  if (singleMatch?.[1]) return singleMatch[1];

  const multipleMatch = text.match(/Required permissions?:\s*([^\n.]+)/i);
  if (multipleMatch?.[1]) {
    const first = multipleMatch[1]
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)[0];
    return first || null;
  }

  return null;
}

/**
 * Turn `material:update` into "edit ingredients".
 * Returns null when either half is unrecognised — callers fall back to generic
 * copy rather than inventing a phrase from a string the user never chose.
 */
export function describeCapability(permission: string | null): string | null {
  if (!permission) return null;

  const [resource, action] = permission.split(':');
  if (!resource || !action) return null;

  // A wildcard grant is about breadth, not a single action; there is no honest
  // short phrase for "everything", so fall back to the generic message.
  if (resource === '*' || action === '*') return null;

  const noun = resourceNoun(resource);
  const verb = actionVerb(action);
  if (!noun || !verb) return null;

  return `${verb} ${noun}`;
}

/** Toast title for a 403. Names the action when we can, stays vague when we can't. */
export function permissionDeniedTitle(serverMessage?: string): string {
  const capability = describeCapability(extractMissingPermission(serverMessage));
  return capability
    ? t`You don't have permission to ${capability}`
    : t`You don't have permission for this action`;
}

/** Toast description for a 403. Points at a person the customer can actually reach. */
export function permissionDeniedDescription(): string {
  return t`Contact your organization owner to request access.`;
}
